import {
  Button,
  Checkbox,
  Container,
  CssBaseline,
  Grid,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Theme,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material'
import React, { useEffect } from 'react'
import { useNavigate } from 'react-router'
import User, { UserType } from 'redux/models/user'
import { ListData, Order, UpdateListPayload } from 'redux/modules/users'
import { ListActions, ListStoreStates } from '.'
import { CSVButton, FormControl, IdText, MySelfLabel, UserTypeLabel, VisuallyHidden } from './styles'

type OwnProps = {}
type ListProps = OwnProps & ListStoreStates & ListActions

type ListHeader = {
  id: keyof ListData
  label: string
}

const List = (props: ListProps) => {
  const navigate = useNavigate()
  const { auth, users, updateList } = props
  const { order, orderBy, sortedItems, listItems, page, rowsPerPage } = users.list

  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  const [selectedUsers, setSelectedUsers] = React.useState<User[]>([])
  const [preDeleting, setPreDeleting] = React.useState(false)

  const numSelected = selectedUsers.length
  const rowCount = listItems.length

  const csvData = users.items.map((item) => item.csvData)
  csvData.unshift(User.csvHeader)

  const onSort = (orderBy: keyof ListData, order?: Order) => {
    const { users, updateList } = props
    const { list } = users

    const isAsc = list.orderBy === orderBy && list.order === 'asc'
    const page = 0

    const payload: UpdateListPayload = order
      ? { orderBy, order, page }
      : { orderBy, order: isAsc ? 'desc' : 'asc', page }

    updateList(payload)
  }

  const onSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { users } = props
    const { listItems } = users.list
    setSelectedUsers(event.target.checked ? listItems : [])
  }

  const onSelectCheckbox = (user: User) => {
    if (!selectedUsers.find((u) => u.id === user.id)) {
      setSelectedUsers(selectedUsers.concat(user))
    } else {
      setSelectedUsers(selectedUsers.filter((u) => u.id !== user.id))
    }
  }

  const onSelectRow = (user: User) => {
    navigate(`/users/${user.id}`)
  }

  const onDeleteUsers = () => {
    const { deleteUsers } = props
    const num = selectedUsers.length
    if (!window.confirm(`${num}人のユーザーを削除してもよろしいですか?`)) return

    deleteUsers(selectedUsers)
    setSelectedUsers([])
  }

  useEffect(() => {
    if (!users.loaded) props.load()
  }, [])

  useEffect(() => {
    if (!preDeleting || users.deletion.deleting) {
      setPreDeleting(false)
      return
    }

    if (!users.deletion.isSucceeded) {
      window.alert('エラーが発生したため削除できませんでした')
    }

    setPreDeleting(users.deletion.deleting)
  }, [preDeleting, users.deletion.deleting, users.deletion.isSucceeded])

  if (users.loading) return <></>

  if (users.deletion.deleting)
    return (
      <Container>
        <CssBaseline />
        <Typography variant="h4" align="center">
          ユーザーを削除中
        </Typography>
        <Typography variant="body1" align="center">
          画面を離れずにお待ちください。
        </Typography>
      </Container>
    )

  const headCells: ListHeader[] = [
    { id: 'name', label: '名前' },
    { id: 'email', label: 'Email' },
    { id: 'createdAt', label: '登録日時' },
  ]

  return (
    <Container>
      <CssBaseline />
      <Paper>
        {!mdDown && (
          <Toolbar>
            <Grid size="grow" />
            <Grid>
              <Button variant="contained" component="label" color="primary">
                <CSVButton filename="users.csv" target="_blank" data={csvData} />
                CSV エクスポート
              </Button>
            </Grid>
          </Toolbar>
        )}

        {!mdUp && (
          <Toolbar>
            <Grid size="grow"></Grid>
            <Grid>
              <FormControl size="small">
                <Select
                  native
                  variant="standard"
                  value={`${orderBy}-${order}`}
                  onChange={(e) => {
                    const value = e.target.value as string
                    const [orderBy, order] = value.split('-')
                    onSort(orderBy as keyof ListData, order as Order)
                  }}>
                  <option value="name-asc">名前（昇順）</option>
                  <option value="name-desc">名前（降順）</option>
                  <option value="email-asc">Email（昇順）</option>
                  <option value="email-desc">Email（降順）</option>
                  <option value="createdAt-asc">登録日時（昇順）</option>
                  <option value="createdAt-desc">登録日時（降順）</option>
                </Select>
              </FormControl>
            </Grid>
          </Toolbar>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              {!mdUp && (
                <TableRow>
                  <TableCell>名前</TableCell>
                </TableRow>
              )}
              {!mdDown && (
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={numSelected > 0 && numSelected < rowCount}
                      checked={rowCount > 0 && numSelected === rowCount}
                      onChange={(e) => onSelectAll(e)}
                      inputProps={{ 'aria-label': 'select all users' }}
                    />
                  </TableCell>
                  {headCells.map((headCell) => (
                    <TableCell key={headCell.id} sortDirection={orderBy === headCell.id ? order : false}>
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => onSort(headCell.id)}>
                        {headCell.label}
                        {orderBy === headCell.id ? (
                          <VisuallyHidden>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</VisuallyHidden>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {listItems.map((user) => {
                const isItemSelected = !!selectedUsers.find((u) => u.id === user.id)
                const labelId = `enhanced-table-checkbox-${user.id}`

                return (
                  <TableRow key={user.id} hover tabIndex={-1}>
                    <TableCell padding="checkbox" className="selectCheckbox">
                      <Checkbox
                        className="selectCheckbox"
                        checked={isItemSelected}
                        inputProps={{ 'aria-labelledby': labelId }}
                        onChange={() => onSelectCheckbox(user)}
                      />
                    </TableCell>
                    <TableCell component="th" scope="row" onClick={() => onSelectRow(user)}>
                      {user.name}&emsp;
                      {user.type !== UserType.User && (
                        <UserTypeLabel component="span">[{user.userTypeText}]&emsp;</UserTypeLabel>
                      )}
                      {auth.user?.id === user.id && <MySelfLabel component="span">(You)</MySelfLabel>}
                      {!mdDown && <IdText>{user.id}</IdText>}
                      {!mdUp && <IdText>{user.email}</IdText>}
                    </TableCell>
                    {!mdDown && (
                      <>
                        <TableCell onClick={() => onSelectRow(user)}>{user.email}</TableCell>
                        <TableCell onClick={() => onSelectRow(user)}>{user.createdDate}</TableCell>
                      </>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Grid container direction="row">
          <Grid size="grow">
            {numSelected > 0 && (
              <Button variant="contained" color="secondary" sx={{ m: 2 }} onClick={() => onDeleteUsers()}>
                選択したユーザーを削除
              </Button>
            )}
          </Grid>
          <Grid>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={sortedItems.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, page) => updateList({ page })}
              onRowsPerPageChange={(e) => {
                updateList({
                  page: 0,
                  rowsPerPage: parseInt(e.target.value) || 10,
                })
              }}
            />
          </Grid>
        </Grid>
      </Paper>
    </Container>
  )
}

export default List
